import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';

const STORAGE_URL = 'https://ymjeinhylrufskwhzobk.supabase.co/storage/v1/object/public/bible/rvr1960';

interface BookData {
  abbrev: string;
  name: string;
  chapters: string[][];
}

const BOOKS_MAP: Record<string, string> = {
  genesis: 'Génesis', exodo: 'Éxodo', levitico: 'Levítico', numeros: 'Números',
  deuteronomio: 'Deuteronomio', josue: 'Josué', jueces: 'Jueces', rut: 'Rut',
  '1-samuel': '1 Samuel', '2-samuel': '2 Samuel', '1-reyes': '1 Reyes',
  '2-reyes': '2 Reyes', '1-cronicas': '1 Crónicas', '2-cronicas': '2 Crónicas',
  esdras: 'Esdras', nehemias: 'Nehemías', ester: 'Ester', job: 'Job',
  salmos: 'Salmos', proverbios: 'Proverbios', eclesiastes: 'Eclesiastés',
  cantares: 'Cantares', isaias: 'Isaías', jeremias: 'Jeremías',
  lamentaciones: 'Lamentaciones', ezequiel: 'Ezequiel', daniel: 'Daniel',
  oseas: 'Oseas', joel: 'Joel', amos: 'Amos', abdias: 'Abdías',
  jonas: 'Jonás', miqueas: 'Miqueas', nahum: 'Nahum', habacuc: 'Habacuc',
  sofonias: 'Sofonías', hageo: 'Hageo', zacarias: 'Zacarías', malaquias: 'Malaquías',
  mateo: 'Mateo', marcos: 'Marcos', lucas: 'Lucas', juan: 'Juan',
  hechos: 'Hechos', romanos: 'Romanos', '1-corintios': '1 Corintios',
  '2-corintios': '2 Corintios', galatas: 'Gálatas', efesios: 'Efesios',
  filipenses: 'Filipenses', colosenses: 'Colosenses', '1-tesalonicenses': '1 Tesalonicenses',
  '2-tesalonicenses': '2 Tesalonicenses', '1-timoteo': '1 Timoteo',
  '2-timoteo': '2 Timoteo', tito: 'Tito', filemon: 'Filemón',
  hebreos: 'Hebreos', santiago: 'Santiago', '1-pedro': '1 Pedro',
  '2-pedro': '2 Pedro', '1-juan': '1 Juan', '2-juan': '2 Juan',
  '3-juan': '3 Juan', judas: 'Judas', apocalipsis: 'Apocalipsis',
};

export default function BibleVerse() {
  const { book, chapter, verse } = useParams<{ book: string; chapter: string; verse: string }>();
  const [data, setData] = useState<BookData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const currentChapter = parseInt(chapter || '1');
  const currentVerse = parseInt(verse || '1');
  const bookName = BOOKS_MAP[book || ''] || book;

  useEffect(() => {
    const fetchChapter = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${STORAGE_URL}/${book}.json`);
        if (!response.ok) throw new Error('No se pudo cargar');
        const json: BookData = await response.json();
        setData(json);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error');
      } finally {
        setLoading(false);
      }
    };
    if (book) fetchChapter();
  }, [book]);

  const handleShare = async () => {
    if (!data) return;
    const verses = data.chapters[currentChapter - 1] || [];
    const verseText = verses[currentVerse - 1] || '';
    const text = `${bookName} ${chapter}:${verse}\n\n${verseText}\n\n— Reina Valera 1960`;

    if (navigator.share) {
      try {
        await navigator.share({ text });
      } catch {
        // User cancelled or error
      }
    } else {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-6 bg-[#3A3A3C] rounded w-32 animate-pulse"></div>
        <div className="h-20 bg-[#3A3A3C] rounded w-full animate-pulse"></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center py-8">
        <p className="text-red-500">{error || 'Error'}</p>
        <Link to={`/bible/${book}/${chapter}`} className="text-primary mt-4 inline-block">← Volver</Link>
      </div>
    );
  }

  const chapters = data.chapters;
  const verses = chapters[currentChapter - 1] || [];
  const totalVerses = verses.length;
  const verseText = verses[currentVerse - 1] || '';
  const prevVerse = currentVerse > 1 ? currentVerse - 1 : null;
  const nextVerse = currentVerse < totalVerses ? currentVerse + 1 : null;

  return (
    <div className="space-y-4">
      <Link to={`/bible/${book}/${chapter}`} className="text-primary">← Volver al capítulo</Link>

      <div className="card p-6">
        <h1 className="text-xl font-bold text-primary mb-4">
          {bookName} {chapter}:{verse}
        </h1>

        {prevVerse && (
          <Link
            to={`/bible/${book}/${currentChapter}/${prevVerse}`}
            className="block text-sm text-[#8E8E93] mb-3 hover:text-primary"
          >
            <span className="font-bold">{prevVerse}</span> {verses[prevVerse - 1]}
          </Link>
        )}

        <p className="text-lg text-white leading-relaxed mb-3">
          <span className="text-primary font-bold text-2xl mr-2">{currentVerse}</span>
          {verseText}
        </p>

        {nextVerse && (
          <Link
            to={`/bible/${book}/${currentChapter}/${nextVerse}`}
            className="block text-sm text-[#8E8E93] hover:text-primary"
          >
            <span className="font-bold">{nextVerse}</span> {verses[nextVerse - 1]}
          </Link>
        )}

        <button
          onClick={handleShare}
          className="mt-6 btn-primary w-full"
        >
          {copied ? '¡Copiado!' : 'Compartir'}
        </button>
      </div>
    </div>
  );
}
